/**
 * Sold Materials Service
 * Handles tracking and management of sold materials with real-time auto-removal
 */

import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  writeBatch,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { getInventoryItems, updateInventoryItem } from './inventoryService';

const db = getFirestore();

/**
 * Add sold materials to tracking collection
 * @param {Array} soldItems - Array of sold material items
 * @param {string} invoiceId - Original invoice ID
 * @returns {Promise<boolean>} Success status
 */
export const addSoldMaterials = async (soldItems, invoiceId) => {
  try {
    console.log('📦 Adding sold materials:', { soldItems, invoiceId });
    
    const batch = writeBatch(db);
    const soldMaterialsRef = collection(db, 'soldMaterials');
    
    for (const item of soldItems) {
      const soldMaterialDoc = {
        materialType: item.materialType,
        quantity: item.quantity,
        soldDate: item.soldDate || new Date().toISOString().split('T')[0],
        originalInvoiceId: invoiceId,
        createdAt: serverTimestamp(),
        // Additional tracking fields
        unitPrice: item.unitPrice || 0,
        totalValue: (item.quantity || 0) * (item.unitPrice || 0)
      };
      
      const newDocRef = doc(soldMaterialsRef);
      batch.set(newDocRef, soldMaterialDoc);
    }
    
    await batch.commit();
    console.log('✅ Sold materials added successfully');
    
    return true;
  } catch (error) {
    console.error('❌ Error adding sold materials:', error);
    return false;
  }
};

/**
 * Get sold materials for a specific date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of sold materials
 */
export const getSoldMaterialsByDateRange = async (startDate, endDate) => {
  try {
    const soldMaterialsRef = collection(db, 'soldMaterials');
    const q = query(
      soldMaterialsRef,
      where('soldDate', '>=', startDate),
      where('soldDate', '<=', endDate),
      orderBy('soldDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const soldMaterials = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📊 Found ${soldMaterials.length} sold materials between ${startDate} and ${endDate}`);
    return soldMaterials;
  } catch (error) {
    console.error('❌ Error getting sold materials by date range:', error);
    return [];
  }
};

/**
 * Get total sold quantities by material type
 * @param {string} materialType - Material type (optional, if not provided returns all)
 * @returns {Promise<Object>} Object with material types as keys and total sold quantities as values
 */
export const getTotalSoldQuantities = async (materialType = null) => {
  try {
    const soldMaterialsRef = collection(db, 'soldMaterials');
    let q;
    
    if (materialType) {
      q = query(soldMaterialsRef, where('materialType', '==', materialType));
    } else {
      q = query(soldMaterialsRef);
    }
    
    const snapshot = await getDocs(q);
    const soldTotals = {};
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const type = data.materialType;
      const quantity = data.quantity || 0;
      
      if (!soldTotals[type]) {
        soldTotals[type] = 0;
      }
      soldTotals[type] += quantity;
    });
    
    console.log('📊 Total sold quantities:', soldTotals);
    return soldTotals;
  } catch (error) {
    console.error('❌ Error getting total sold quantities:', error);
    return {};
  }
};

/**
 * Process purchase invoice for auto-removal when date passes
 * @param {Object} invoice - Purchase invoice object
 * @returns {Promise<boolean>} Success status
 */
export const processPurchaseInvoiceAutoRemoval = async (invoice) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    let purchaseDate;
    
    // Extract purchase date from invoice
    if (invoice.leadTime) {
      purchaseDate = invoice.leadTime;
    } else if (invoice.dates && Array.isArray(invoice.dates)) {
      // Find purchase date in unified dates array
      const purchaseDateEntry = invoice.dates.find(d => d.type === 'date' && d.value);
      purchaseDate = purchaseDateEntry?.value;
    }
    
    if (!purchaseDate) {
      console.log('⚠️ No purchase date found in invoice');
      return false;
    }
    
    // Check if purchase date has passed
    if (purchaseDate <= today) {
      console.log(`🔄 Processing auto-removal for purchase invoice ${invoice.invoiceNumber} (date: ${purchaseDate})`);
      
      // Extract materials from line items
      const soldItems = [];
      if (invoice.lineItems && Array.isArray(invoice.lineItems)) {
        invoice.lineItems.forEach(item => {
          if (item.materialType && item.quantity > 0) {
            soldItems.push({
              materialType: item.materialType,
              quantity: item.quantity,
              soldDate: purchaseDate,
              unitPrice: item.unitPrice || 0
            });
          }
        });
      }
      
      if (soldItems.length > 0) {
        // Add to sold materials tracking
        await addSoldMaterials(soldItems, invoice.id);
        
        // Remove from inventory
        await removeFromInventory(soldItems);
        
        console.log(`✅ Auto-removed ${soldItems.length} material types from inventory`);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('❌ Error processing purchase invoice auto-removal:', error);
    return false;
  }
};

/**
 * Remove sold materials from inventory
 * @param {Array} soldItems - Array of sold material items
 * @returns {Promise<boolean>} Success status
 */
const removeFromInventory = async (soldItems) => {
  try {
    console.log('📦 Removing sold materials from inventory:', soldItems);
    
    for (const item of soldItems) {
      // Get current inventory for this material type
      const inventoryItems = await getInventoryItems({ materialType: item.materialType });
      
      if (inventoryItems.length > 0) {
        const currentInventory = inventoryItems[0]; // Should only be one item per material type
        
        // Calculate new quantities (remove from new items first, then used)
        let remainingToRemove = item.quantity;
        let newNewQuantity = currentInventory.newQuantity || 0;
        let newUsedQuantity = currentInventory.usedQuantity || 0;
        
        // Remove from new quantity first
        if (remainingToRemove > 0 && newNewQuantity > 0) {
          const removeFromNew = Math.min(remainingToRemove, newNewQuantity);
          newNewQuantity -= removeFromNew;
          remainingToRemove -= removeFromNew;
        }
        
        // Remove remaining from used quantity
        if (remainingToRemove > 0 && newUsedQuantity > 0) {
          const removeFromUsed = Math.min(remainingToRemove, newUsedQuantity);
          newUsedQuantity -= removeFromUsed;
          remainingToRemove -= removeFromUsed;
        }
        
        // Update inventory using the correct function
        const updatedQuantities = {
          newQuantity: Math.max(0, newNewQuantity),
          usedQuantity: Math.max(0, newUsedQuantity),
          totalQuantity: Math.max(0, newNewQuantity) + Math.max(0, newUsedQuantity) + (currentInventory.damagedQuantity || 0)
        };
        
        await updateInventoryItem(currentInventory.id, updatedQuantities);
        
        console.log(`✅ Updated inventory for ${item.materialType}:`, updatedQuantities);
        
        if (remainingToRemove > 0) {
          console.warn(`⚠️ Could not remove ${remainingToRemove} units of ${item.materialType} - insufficient inventory`);
        }
      } else {
        console.warn(`⚠️ No inventory found for material type: ${item.materialType}`);
      }
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error removing from inventory:', error);
    return false;
  }
};

/**
 * Check and process all purchase invoices for auto-removal
 * @param {Array} allInvoices - Array of all invoices
 * @returns {Promise<number>} Number of invoices processed
 */
export const checkAllPurchaseInvoicesForAutoRemoval = async (allInvoices) => {
  try {
    console.log('🔍 Checking all purchase invoices for auto-removal...');
    
    let processedCount = 0;
    const purchaseInvoices = allInvoices.filter(invoice => 
      invoice.invoiceType === 'purchase' && 
      invoice.status !== 'cancelled'
    );
    
    for (const invoice of purchaseInvoices) {
      const processed = await processPurchaseInvoiceAutoRemoval(invoice);
      if (processed) {
        processedCount++;
      }
    }
    
    console.log(`✅ Processed ${processedCount} purchase invoices for auto-removal`);
    return processedCount;
  } catch (error) {
    console.error('❌ Error checking purchase invoices for auto-removal:', error);
    return 0;
  }
};

/**
 * Set up real-time listener for purchase invoice auto-removal
 * @param {Function} onProcessed - Callback when invoices are processed
 * @returns {Function} Unsubscribe function
 */
export const setupAutoRemovalListener = (onProcessed) => {
  try {
    console.log('👂 Setting up auto-removal listener...');
    
    // Listen to invoice changes
    const invoicesRef = collection(db, 'invoices');
    const q = query(
      invoicesRef,
      where('invoiceType', '==', 'purchase'),
      where('status', '!=', 'cancelled')
    );
    
    return onSnapshot(q, async (snapshot) => {
      const invoices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`📊 Checking ${invoices.length} purchase invoices for auto-removal...`);
      
      const processedCount = await checkAllPurchaseInvoicesForAutoRemoval(invoices);
      
      if (processedCount > 0 && onProcessed) {
        onProcessed(processedCount);
      }
    });
    
  } catch (error) {
    console.error('❌ Error setting up auto-removal listener:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Get sold materials statistics
 * @param {string} startDate - Start date for statistics (optional)
 * @param {string} endDate - End date for statistics (optional)
 * @returns {Promise<Object>} Statistics object
 */
export const getSoldMaterialsStatistics = async (startDate = null, endDate = null) => {
  try {
    const soldMaterialsRef = collection(db, 'soldMaterials');
    let q;
    
    if (startDate && endDate) {
      q = query(
        soldMaterialsRef,
        where('soldDate', '>=', startDate),
        where('soldDate', '<=', endDate),
        orderBy('soldDate', 'desc')
      );
    } else {
      q = query(soldMaterialsRef, orderBy('soldDate', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    const soldMaterials = snapshot.docs.map(doc => doc.data());
    
    const stats = {
      totalTransactions: soldMaterials.length,
      totalQuantity: 0,
      totalValue: 0,
      byMaterialType: {},
      byDate: {},
      dateRange: { startDate, endDate }
    };
    
    soldMaterials.forEach(item => {
      const quantity = item.quantity || 0;
      const value = item.totalValue || 0;
      const materialType = item.materialType;
      const soldDate = item.soldDate;
      
      // Total stats
      stats.totalQuantity += quantity;
      stats.totalValue += value;
      
      // By material type
      if (!stats.byMaterialType[materialType]) {
        stats.byMaterialType[materialType] = {
          quantity: 0,
          value: 0,
          transactions: 0
        };
      }
      stats.byMaterialType[materialType].quantity += quantity;
      stats.byMaterialType[materialType].value += value;
      stats.byMaterialType[materialType].transactions += 1;
      
      // By date
      if (!stats.byDate[soldDate]) {
        stats.byDate[soldDate] = {
          quantity: 0,
          value: 0,
          transactions: 0
        };
      }
      stats.byDate[soldDate].quantity += quantity;
      stats.byDate[soldDate].value += value;
      stats.byDate[soldDate].transactions += 1;
    });
    
    console.log('📊 Sold materials statistics:', stats);
    return stats;
  } catch (error) {
    console.error('❌ Error getting sold materials statistics:', error);
    return {
      totalTransactions: 0,
      totalQuantity: 0,
      totalValue: 0,
      byMaterialType: {},
      byDate: {},
      error: error.message
    };
  }
};

export default {
  addSoldMaterials,
  getSoldMaterialsByDateRange,
  getTotalSoldQuantities,
  processPurchaseInvoiceAutoRemoval,
  checkAllPurchaseInvoicesForAutoRemoval,
  setupAutoRemovalListener,
  getSoldMaterialsStatistics
};
